import { FeatureToggles } from '@/features/feature-toggles'
import type { DocumentType } from '@/features/documents/DocumentType'
import { ModalViewModel } from '@/infra/ModalViewModel'
import { type Job } from '@/features/jobs/Job'
import { type JobProgressionStore } from '@/features/jobs/taxes/progression/JobProgressionStore'
import { task } from 'mobx-task'
import type { AlertErrorHandlerFactory } from '@/features/shell'

/**
 * Moves the job to open items, asking for the missing documents when document requests are on.
 */
export class OpenItemsDialogViewModel {
  readonly modal = new ModalViewModel()

  constructor(
    private readonly _progressionStore: JobProgressionStore,
    private readonly _promptForDocumentRequest: () => Promise<
      DocumentType[] | null
    >,
    private readonly _job: Job,
    private readonly _createAlertErrorHandler: AlertErrorHandlerFactory,
  ) {}

  /**
   * Moves the job to open items and closes the dialog.
   */
  answerOpenItems = task.resolved(async () => {
    await this._progressionStore
      .transitionToStep(this._job.id, { step: 'OpenItems' })
      .catch(
        this._createAlertErrorHandler('Moving the job to open items failed'),
      )
    this.modal.close()
  })

  /**
   * Shows the dialog, or the document request prompt when document requests are on.
   */
  async show() {
    const documentRequestEnabled = FeatureToggles.documentRequest()

    if (!documentRequestEnabled) {
      this.modal.show()
      return
    }

    const result = await this._promptForDocumentRequest()
    if (result) {
      await this.answerOpenItems()
    }
  }
}
